import {
  addDays,
  endOfDay,
  endOfMonth,
  format,
  getDay,
  isAfter,
  isBefore,
  isSameMonth,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMinutes,
  subWeeks,
} from 'date-fns'
import { bucketize } from '@opallabs/kuiper'

interface BucketMeta {
  matcher: (value: Date) => boolean
  start: Date | ((value: Date) => Date)
  subTitle?: string | ((value: Date) => string)
  timeFormat?: string
  title?: string | ((value: Date) => string)
}

const futureBucket = (referenceTime: Date): BucketMeta => ({
  timeFormat: 'p',
  start: startOfDay(addDays(referenceTime, 1)),
  matcher(value): boolean {
    return isAfter(value, endOfDay(referenceTime))
  },
})

const todayBucket = (referenceTime: Date): BucketMeta => ({
  title: 'Today',
  timeFormat: 'p',
  start: startOfDay(referenceTime),
  matcher(value): boolean {
    return isWithinInterval(value, {
      start: startOfDay(referenceTime),
      end: referenceTime,
    })
  },
})

const yesterdayBucket = (referenceTime: Date): BucketMeta => ({
  title: 'Yesterday',
  timeFormat: 'p',
  start: startOfDay(subDays(referenceTime, 1)),
  matcher(value): boolean {
    return isWithinInterval(value, {
      start: startOfDay(subDays(referenceTime, 1)),
      end: subMinutes(startOfDay(referenceTime), 1),
    })
  },
})

const rangeFormat = 'MMMM d'
const dateFormat = (date: Date, aFormat = rangeFormat): string =>
  format(date, aFormat)

const rangeString = (start: Date, end: Date): string => {
  if (isSameMonth(start, end)) {
    return `${dateFormat(start)}–${format(end, 'd')}`
  }

  return `${dateFormat(start)}–${dateFormat(end)}`
}

const thisWeekBucket = (referenceTime: Date): BucketMeta => ({
  title: 'This Week',
  subTitle: rangeString(
    startOfWeek(referenceTime),
    subMinutes(startOfDay(referenceTime), 1)
  ),
  start: startOfWeek(referenceTime),
  matcher(value): boolean {
    return isWithinInterval(value, {
      start: startOfWeek(referenceTime),
      end: referenceTime,
    })
  },
})

const lastWeekBucket = (referenceTime: Date): BucketMeta => ({
  title: 'Last Week',
  subTitle: rangeString(
    startOfWeek(subWeeks(referenceTime, 1)),
    startOfWeek(subDays(referenceTime, 1.1))
  ),
  start: startOfWeek(subWeeks(referenceTime, 1)),
  matcher(value): boolean {
    return isWithinInterval(value, {
      start: startOfWeek(subWeeks(referenceTime, 1)),
      end: subDays(startOfWeek(referenceTime), 1),
    })
  },
})

const monthInThisYearBucket = (referenceTime: Date): BucketMeta => ({
  timeFormat: 'PPP',
  title(value): string {
    return format(value, 'MMMM')
  },
  subTitle(value): string {
    return rangeString(startOfMonth(value), endOfMonth(value))
  },
  start(value): Date {
    return startOfMonth(value)
  },
  matcher(value): boolean {
    return isWithinInterval(value, {
      start: startOfYear(referenceTime),
      end: referenceTime,
    })
  },
})

const monthInPreviousYearBucket = (referenceTime: Date): BucketMeta => ({
  timeFormat: 'PPP',
  title(value): string {
    return format(value, 'MMMM yyyy')
  },
  subTitle(value): string {
    return rangeString(startOfMonth(value), endOfMonth(value))
  },
  start(value): Date {
    return startOfMonth(value)
  },
  matcher(value): boolean {
    return isBefore(value, startOfYear(referenceTime))
  },
})

const startDayBuckets = (referenceTime: Date): BucketMeta[] => [
  futureBucket(referenceTime),
  todayBucket(referenceTime),
  lastWeekBucket(referenceTime),
  monthInThisYearBucket(referenceTime),
  monthInPreviousYearBucket(referenceTime),
]

const secondDayBuckets = (referenceTime: Date): BucketMeta[] => [
  futureBucket(referenceTime),
  todayBucket(referenceTime),
  yesterdayBucket(referenceTime),
  lastWeekBucket(referenceTime),
  monthInThisYearBucket(referenceTime),
  monthInPreviousYearBucket(referenceTime),
]

const defaultBuckets = (referenceTime: Date): BucketMeta[] => [
  futureBucket(referenceTime),
  todayBucket(referenceTime),
  thisWeekBucket(referenceTime),
  lastWeekBucket(referenceTime),
  monthInThisYearBucket(referenceTime),
  monthInPreviousYearBucket(referenceTime),
]

const currentBuckets = (referenceTime: Date): BucketMeta[] => {
  if (getDay(referenceTime) === 0) {
    return startDayBuckets(referenceTime)
  }
  if (getDay(referenceTime) === 1) {
    return secondDayBuckets(referenceTime)
  }
  return defaultBuckets(referenceTime)
}

interface Bucket<T> {
  bucketId: string
  items: Array<T>
  bucket: BucketMeta
}

export function timeBucketizedList<T>(arg: {
  data: T[]
  getValue: (arg: T) => Date
}): Bucket<T>[] {
  const referenceTime = new Date()
  const bucketized = bucketize<T, Date>({
    buckets: currentBuckets(referenceTime),
    data: arg.data,
    getValue: arg.getValue,
  })
  const sortedKeys = Object.keys(bucketized).sort((a, b) => {
    const aDate = new Date(a)
    const bDate = new Date(b)
    if (isAfter(aDate, bDate)) {
      return -1
    }
    if (isBefore(aDate, bDate)) {
      return 1
    }
    return 0
  })
  const sorter = (a: T, b: T): number => {
    if (isBefore(arg.getValue(a), arg.getValue(b))) {
      return 1
    }
    return -1
  }
  return sortedKeys.reduce<Bucket<T>[]>(
    (acc, cur) => [
      ...acc,
      {
        bucketId: `${cur}`,
        bucket: bucketized[cur].bucket,
        getValue: arg.getValue,
        items: bucketized[cur].items.sort(sorter),
      },
    ],
    []
  )
}
